import axios from 'axios';
import type { AdminFlagsResponse } from '@safe-minds-ai/shared-dtos';
import { ToastContextValue } from './toast';

const API_BASE =
  process.env.NX_API_BASE_URL ||
  process.env.API_BASE_URL ||
  process.env.NX_PUBLIC_API_URL ||
  '/api';

const ACCESS_KEY = 'admin_access_token';
const REFRESH_KEY = 'admin_refresh_token';

const client = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

let toastInstance: ToastContextValue | null = null;

export function setToastInstance(instance: ToastContextValue) {
  toastInstance = instance;
}

client.interceptors.request.use((config) => {
  const accessToken = typeof window !== 'undefined' ? localStorage.getItem(ACCESS_KEY) : null;
  if (accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  // For refresh endpoint, we need to send refresh token
  if (config.url === '/auth/refresh') {
    const refreshToken = typeof window !== 'undefined' ? localStorage.getItem(REFRESH_KEY) : null;
    if (refreshToken) {
      config.headers = config.headers || {};
      config.headers['x-refresh-token'] = refreshToken;
    }
  }
  return config;
});

client.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const originalRequest = error.config;
    // Check if the error is 401 (Unauthorized) and not already retrying
    // Also, ensure it's not the refresh token request itself that failed with 401
    if (error.response.status === 401 && !originalRequest._retry && originalRequest.url !== '/auth/refresh') {
      originalRequest._retry = true;
      try {
        // Attempt to refresh the token
        const { data } = await client.post('/auth/refresh');
        setTokens(data.access_token, data.refresh_token);
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        // Retry the original request with the new access token
        return client(originalRequest);
      } catch (refreshError: any) {
        // If refresh fails (e.g., refresh token expired or invalid), clear tokens and redirect to login
        clearTokens();
        toastInstance?.push({ title: 'Session Expired', description: refreshError.response?.data?.message || 'Please log in again.', variant: 'error' });
        window.location.reload(); // Reload to trigger redirection to login
        return Promise.reject(refreshError);
      }
    }

    // Generic error handling with toast
    const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
    toastInstance?.push({ title: 'Error', description: errorMessage, variant: 'error' });
    return Promise.reject(error);
  }
);

export function setTokens(access?: string, refresh?: string) {
  if (access) localStorage.setItem(ACCESS_KEY, access);
  else localStorage.removeItem(ACCESS_KEY);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  else localStorage.removeItem(REFRESH_KEY);
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function getAccessToken(): string | null {
  return typeof window !== 'undefined' ? localStorage.getItem(ACCESS_KEY) : null;
}

export function getRefreshToken(): string | null {
  return typeof window !== 'undefined' ? localStorage.getItem(REFRESH_KEY) : null;
}


export async function loginAdmin(email: string, password:string) {
  const { data } = await client.post('/auth/login', { email, password, role: 'ADMIN' });
  // Expect access_token/refresh_token in data (align with auth-service response)
  setTokens(data?.access_token, data?.refresh_token);
  return data;
}

// ----- Admin APIs -----
export async function fetchOverview(params?: { from?: string; to?: string }) {
  const { data } = await client.get('/admin/analytics/overview', { params });
  return data;
}

export async function fetchUsageByDay(params?: { from?: string; to?: string }) {
  const { data } = await client.get('/admin/analytics/usage', {
    params: { ...(params || {}), groupBy: 'day' },
  });
  return data?.byDay ?? [];
}

export type DashboardRange = { from: string; to: string };
export type DashboardKpi = {
  key: string;
  label: string;
  currentValue: number;
  previousValue: number | null;
  deltaPercent: number | null;
  deltaDirection: 'up' | 'down' | 'flat';
  comparisonLabel: string;
  comparisonRange: DashboardRange;
  currentRange: DashboardRange;
  isPercent?: boolean;
};

export type SafetyScoreBreakdown = {
  key: string;
  label: string;
  weight: number;
  currentValue: number;
  previousValue: number;
  normalizedScore: number;
  contribution: number;
  explanation: string;
};

export type DashboardOverview = {
  range: DashboardRange;
  compareRange: DashboardRange;
  comparisonLabel: string;
  kpis: DashboardKpi[];
  safetyScore: {
    score: number;
    previousScore: number;
    deltaPercent: number | null;
    deltaDirection: 'up' | 'down' | 'flat';
    definition: string;
    lastUpdatedAt: string;
    breakdown: SafetyScoreBreakdown[];
    topDrivers: { improving: string | null; hurting: string | null };
  };
  needsAttention: Array<{
    key: string;
    label: string;
    deltaPercent: number;
    description: string;
    action: { label: string; href: string };
  }>;
  progress: {
    featureAdoption: Array<{ label: string; value: number; total: number }>;
    complianceCoverage: Array<{ label: string; value: number; total: number }>;
    systemHealth: Array<{ label: string; value: number }>;
  };
};

export type DashboardTrendPoint = { day: string; c: number };
export type DashboardTrends = {
  range: DashboardRange;
  compareRange: DashboardRange;
  comparisonLabel: string;
  series: Array<{
    key: string;
    label: string;
    points: DashboardTrendPoint[];
    previousPoints?: DashboardTrendPoint[];
  }>;
};

export type DashboardBreakdownRow = { label: string; c: number };
export type DashboardBreakdowns = {
  range: DashboardRange;
  compareRange: DashboardRange;
  comparisonLabel: string;
  rows: DashboardBreakdownRow[];
  previousRows?: DashboardBreakdownRow[];
};

export async function getDashboardOverview(params: { from?: string; to?: string; compareMode?: string }) {
  const { data } = await client.get<DashboardOverview>('/admin/dashboard/overview', { params });
  return data;
}

export async function getDashboardTrends(params: { from?: string; to?: string; compareMode?: string; granularity?: 'day' }) {
  const { data } = await client.get<DashboardTrends>('/admin/dashboard/trends', { params });
  return data;
}

export async function getDashboardBreakdowns(params: { from?: string; to?: string; compareMode?: string; dimension: string }) {
  const { data } = await client.get<DashboardBreakdowns>('/admin/dashboard/breakdowns', { params });
  return data;
}

export async function fetchFlags(params?: {
  status?: string;
  limit?: number;
  offset?: number;
  search?: string;
  type?: 'MODERATION' | 'CHEATING' | 'ALL';
}): Promise<AdminFlagsResponse> {
  const { data } = await client.get<AdminFlagsResponse>('/admin/flags', { params });
  return data ?? { items: [], pagination: { total: 0, limit: params?.limit ?? 20, offset: params?.offset ?? 0, hasMore: false } };
}

export async function reviewFlag(
  flagId: string,
  payload: {
    reviewStatus: 'APPROVED' | 'REJECTED';
    finalLabel?: 'CHEATING' | 'NOT_CHEATING';
    finalSafetyLabel?: 'SAFETY' | 'NOT_SAFETY';
    finalSafetyType?: 'GENERAL_SAFETY' | 'MENTAL_HEALTH' | 'EXPLICIT_CONTENT';
    finalCategory?: Record<string, unknown> | null;
    reviewedShouldRefuse?: boolean;
    reviewedIntent?: string;
    reviewedCheatingRisk?: string;
    reviewedCheatingType?: string;
    reviewedResponseMode?: string;
    reviewNote?: string;
  },
) {
  const { data } = await client.patch(`/admin/flags/${flagId}`, payload);
  return data;
}

export async function fetchModerationSummary(params?: { from?: string; to?: string }) {
  const { data } = await client.get('/admin/analytics/moderation', { params });
  return data;
}

export async function fetchModelsSummary(params?: { from?: string; to?: string }) {
  const { data } = await client.get('/admin/analytics/models', { params });
  return data;
}

// ----- Plans APIs -----
export interface Plan {
  id: string;
  name: string;
  price_month?: number;
  timeLimitEnabled?: boolean;
  features?: Record<string, unknown>;
  limits?: Record<string, unknown>;
  defaults?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePlanDto {
  id?: string;
  name: string;
  price_month?: number;
  timeLimitEnabled?: boolean;
  features?: Record<string, unknown>;
  limits?: Record<string, unknown>;
  defaults?: Record<string, unknown>;
}

export interface UpdatePlanDto {
  name?: string;
  price_month?: number;
  timeLimitEnabled?: boolean;
  features?: Record<string, unknown>;
  limits?: Record<string, unknown>;
  defaults?: Record<string, unknown>;
}

export async function fetchPlans(): Promise<Plan[]> {
  const { data } = await client.get('/admin/plans');
  return data;
}

export async function createPlan(planData: CreatePlanDto): Promise<Plan> {
  const { data } = await client.post('/admin/plans', planData);
  return data;
}

export async function updatePlan(id: string, planData: UpdatePlanDto): Promise<void> {
  await client.put(`/admin/plans/${id}`, planData);
}

// ----- Users APIs -----
export interface User {
  id: string;
  email: string;
  role: 'ADMIN' | 'PARENT' | 'CHILD';
  approved?: boolean | null;
  isVerified?: boolean | null;
  emailVerifiedAt?: string | null;
  createdAt: string;
  deletedAt?: string | null;
  isDeleted?: boolean;
  parentId?: string | null;
  name?: string | null;
  parentName?: string | null;
  parentEmail?: string | null;
  childrenCount?: number | null;
}

export interface UserDetail extends User {
  updatedAt?: string;
  approvedAt?: string | null;
  approvedByUserId?: string | null;
  parentSummary?: { id: string; name: string | null; email: string | null } | null;
  parentProfile?: { id: string; firstName: string; lastName: string; subscriptionType: string } | null;
  childProfile?: {
    id: string;
    nickname: string;
    firstName?: string | null;
    lastName?: string | null;
    age: number;
    grade?: number | null;
    parentId: string;
    isDeleted: boolean;
    deletedAt?: string | null;
  } | null;
  children?: Array<{ id: string; name: string; email: string | null; isDeleted: boolean; deletedAt?: string | null }>;
}

export interface ParentProfile {
  firstName: string;
  lastName: string;
}

export interface ChildProfile {
  nickname: string;
  firstName?: string;
  lastName?: string;
  age: number;
  grade?: number;
}

export function getAdminChildDisplayName(child?: {
  name?: string | null;
  nickname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
} | null): string {
  return (
    child?.name?.trim() ||
    child?.nickname?.trim() ||
    `${child?.firstName ?? ''} ${child?.lastName ?? ''}`.trim() ||
    'Learner'
  );
}

export interface CreateUserDto {
  email?: string;
  password?: string;
  role: 'ADMIN' | 'PARENT' | 'CHILD';
  parentUserId?: string;
  parentProfile?: ParentProfile;
  childProfile?: ChildProfile;
}

export interface UsersListResponse {
  items: User[];
  stats?: {
    total: number;
    ADMIN: number;
    PARENT: number;
    CHILD: number;
    deleted: number;
  };
  pagination: { total: number; limit: number; offset: number; sort?: string };
}

export async function fetchUsers(params?: {
  role?: string;
  roles?: string;
  approved?: 'true' | 'false' | 'ALL';
  limit?: number;
  offset?: number;
  deletedFilter?: 'active' | 'deleted' | 'all';
  parentId?: string;
  search?: string;
  sortBy?: 'createdAt' | 'name' | 'role' | 'status' | 'deletedAt';
  sortOrder?: 'asc' | 'desc';
  includeDeleted?: boolean;
}): Promise<UsersListResponse> {
  const { data } = await client.get('/admin/users', { params });
  return data;
}

export async function fetchUserDetail(userId: string): Promise<UserDetail> {
  const { data } = await client.get(`/admin/users/${userId}`);
  return data;
}

export async function createUser(userData: CreateUserDto): Promise<User> {
  const { data } = await client.post('/admin/users', userData);
  return data;
}

export interface UpdateUserPayload {
  role?: User['role'];
  approved?: boolean;
  disable?: boolean;
  enable?: boolean;
  parentUserId?: string;
  parentProfile?: ParentProfile;
  childProfile?: ChildProfile;
}

export async function updateUser(userId: string, payload: UpdateUserPayload): Promise<User> {
  const { data } = await client.patch(`/admin/users/${userId}`, payload);
  return data;
}

export async function resendUserVerificationEmail(userId: string): Promise<{ message: string }> {
  const { data } = await client.post<{ message: string }>(`/admin/users/${userId}/resend-verification`);
  return data;
}

export async function resendUserConsentEmail(userId: string): Promise<{ message: string }> {
  const { data } = await client.post<{ message: string }>(`/admin/users/${userId}/resend-consent`);
  return data;
}

export async function downloadParentEmailExport(audience: 'all' | 'marketing'): Promise<void> {
  const response = await client.get('/admin/users/export/parents', {
    params: { audience },
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = audience === 'marketing' ? 'marketing-opted-in-parents.csv' : 'all-parent-users.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ----- Moderation APIs -----
export interface ModerationConfig {
  id: string;
  thresholds: Record<string, unknown>;
  level_map: Record<string, unknown>;
  createdAt?: string; // Add if available from backend
  updatedAt?: string; // Add if available from backend
}

export async function fetchModerationConfig(): Promise<ModerationConfig> {
  const { data } = await client.get('/admin/moderation/config');
  return data;
}

export async function updateModerationConfig(configData: { thresholds?: Record<string, unknown>; level_map?: Record<string, unknown> }): Promise<ModerationConfig> {
  const { data } = await client.patch('/admin/moderation/config', configData);
  return data;
}

// ----- Support APIs -----
export const SUPPORT_REQUEST_STATUSES = ['OPEN', 'IN_PROGRESS', 'RESOLVED'] as const;
export type SupportRequestStatus = (typeof SUPPORT_REQUEST_STATUSES)[number];

export interface SupportRequest {
  id: string;
  parentId: string;
  name?: string | null;
  email?: string | null;
  subject: string;
  message: string;
  category?: string | null;
  status: SupportRequestStatus;
  attachments?: SupportRequestAttachment[] | null;
  createdAt: string;
}

export interface SupportRequestAttachment {
  name: string;
  type: string;
  size: number;
  content?: string;
  s3Key?: string;
  url?: string;
}

export async function fetchSupportRequests(status?: SupportRequestStatus | 'ALL'): Promise<SupportRequest[]> {
  const { data } = await client.get('/admin/support-requests', {
    params: status && status !== 'ALL' ? { status } : undefined,
  });
  return data;
}

export async function updateSupportRequestStatus(
  supportRequestId: string,
  status: SupportRequestStatus,
): Promise<SupportRequest> {
  const { data } = await client.patch<SupportRequest>(`/admin/support-requests/${supportRequestId}/status`, { status });
  return data;
}

export async function fetchSupportAttachmentDownloadUrl(
  supportRequestId: string,
  s3Key: string,
): Promise<{ downloadUrl: string; s3Key: string }> {
  const { data } = await client.post<{ downloadUrl: string; s3Key: string }>(
    `/admin/support-requests/${supportRequestId}/attachments/presign-download`,
    { s3Key },
  );
  return data;
}

export function logout() {
  clearTokens();
  // No backend call for logout in this example; client-side token clear is sufficient for now.
  // If a backend invalidation of refresh token is needed, it would go here.
}
